<template>
  <table class="ui-table rd-tbl gm-matrix">
    <thead>
      <tr>
        <th rowspan="2" scope="col" class="gm-day">{{ $t("day") }}</th>
        <th
          v-for="category in filteredMealCategories"
          :key="category"
          :colspan="shiftList.length + 1"
          scope="colgroup"
          class="gm-cat gm-first"
          :title="categoryText(category)"
        >
          {{ categoryText(category) }}
        </th>
        <th rowspan="2" scope="col" class="gm-first gm-sum">{{ $t("total") }}</th>
      </tr>
      <tr>
        <template v-for="category in filteredMealCategories">
          <th
            v-for="(shift, index) in shiftList"
            :key="`${category}_${shift}`"
            scope="col"
            :class="{ 'gm-first': index === 0 }"
          >
            <span class="rs-tag gm-num" :data-shift="shift">
              <span aria-hidden="true">{{ shift }}</span>
              <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: shift }) }}</span>
            </span>
          </th>
          <th :key="`${category}_total`" scope="col" class="gm-sum" :title="$t('total')">Σ</th>
        </template>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(row, index) in dayRows" :key="row.day">
        <td class="gm-day" :class="{ 'is-weekend': isWeekend(index) }">{{ dayText(index) }}</td>
        <template v-for="category in filteredMealCategories">
          <td
            v-for="(shift, shiftIndex) in shiftList"
            :key="`${category}_${shift}`"
            :class="{ 'is-zero': !row[`${category}_${shift}`], 'gm-first': shiftIndex === 0 }"
          >
            {{ row[`${category}_${shift}`] || "–" }}
          </td>
          <td :key="`${category}_total`" class="gm-sum" :class="{ 'is-zero': !row[`${category}_total`] }">
            {{ row[`${category}_total`] || "–" }}
          </td>
        </template>
        <td class="gm-first gm-sum" :class="{ 'is-zero': !row.grandTotal }">{{ row.grandTotal || "–" }}</td>
      </tr>
    </tbody>
    <tfoot v-if="totalRow">
      <tr>
        <td class="gm-day">{{ $t("total") }}</td>
        <template v-for="category in filteredMealCategories">
          <td
            v-for="(shift, shiftIndex) in shiftList"
            :key="`${category}_${shift}`"
            :class="{ 'gm-first': shiftIndex === 0 }"
          >
            {{ totalRow[`${category}_${shift}`] || 0 }}
          </td>
          <td :key="`${category}_total`" class="gm-sum">{{ totalRow[`${category}_total`] || 0 }}</td>
        </template>
        <td class="gm-first gm-sum">{{ totalRow.grandTotal || 0 }}</td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
import GroupedShiftMealsTable from "@/components/report/shared/GroupedShiftMealsTable.vue";

/**
 * Tabela "Grupe jela" (dan × grupa jela × smena) novog izgleda: nasleđuje podatke, zbirove i redove za štampu
 * i Excel iz GroupedShiftMealsTable (stari tab ih čita preko ref-a), samo drugačije prikazuje. Prikazuju se samo
 * izabrane smene, prvi stub (dan) ostaje pri horizontalnom skrolu, a nule su crtice.
 */
export default {
  name: "ReportDesktopGroupedMatrix",
  extends: GroupedShiftMealsTable,
  computed: {
    /** Izabrane smene; prazan izbor znači sve (kao u starom prikazu). */
    shiftList() {
      const list = [1, 2, 3, 4].filter((shift) => this.selectedShift.includes(shift));
      return list.length ? list : [1, 2, 3, 4];
    },
    dayRows() {
      return this.groupedData.filter((row) => !row.isTotal);
    },
    totalRow() {
      return this.groupedData.find((row) => row.isTotal) || null;
    },
    formatLocale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
  },
  methods: {
    categoryText(category) {
      return this.toLatin(this.categoryLabel(category));
    },
    dateOf(index) {
      const [year, month, day] = String(this.dateArray[index] || "")
        .split("-")
        .map(Number);
      return year ? new Date(year, month - 1, day) : null;
    },
    /** "pon 20.10." – dan u nedelji i datum, da se red prepozna bez računanja. */
    dayText(index) {
      const date = this.dateOf(index);
      if (!date) {
        return this.dayRows[index]?.day || "";
      }
      const weekday = new Intl.DateTimeFormat(this.formatLocale, { weekday: "short" }).format(date).replace(/\.$/, "");
      const short = new Intl.DateTimeFormat(this.formatLocale, { day: "numeric", month: "numeric" }).format(date);
      return `${weekday} ${short}`;
    },
    isWeekend(index) {
      const date = this.dateOf(index);
      return Boolean(date) && [0, 6].includes(date.getDay());
    },
  },
};
</script>
